import { useMemo, useCallback } from "react"
import { map, chain, reduce, capitalize, compact, camelCase, isFunction } from "lodash"
import { INSTRUMENTS } from "connected-components/dashboard/helpers"
import { getTradePosition } from "connected-components/dashboard/helpers/trades"
import { CONTENT_SET_DATA_TYPES } from "helpers/contentSetConstants"
import { useModelBetaFields } from "resources/beta"
import { useContentSetsMetadata } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useResourcesList } from "resources/resource"
import { useIsOpPro } from "resources/account"
import { BETA_FACTORS_WITH_DYNAMIC } from "helpers/analyzeConstants"
import { combineTanStackStatuses } from "helpers/data-statuses"
import { METRIC_OPTIONS } from "./definitions"
import { VALUE_FORMATS } from "./constants"
import { ASSET_METRIC_CATEGORIES, getMarginalContributionMetrics, ASSET_METRICS_BY_ID } from "./assets"
import { FACTOR_METRIC_CATEGORIES, FACTOR_METRICS_BY_ID } from "./factors"
import { makeDataPath, isInstrumentWithoutGMV, filterColumnsBasedOnGMV, createMetricGroup, createMetricTypeOptions, hasAction, makeMetricLabel, getDefaultDownloadMetrics, getDataGetter, makeTooltipText, makeTooltipContents, makeDownloadLabel, getFilteredTriplicatedColumns } from "./utils"
import { getRiskModelMetrics } from "./index"
import { RULES } from "connected-components/compare/position-sets"

export function useAssetViewDashboardMetrics(metricsAndCategories, originalInstrument, rebalancedInstrument, comparisonInstrument, options = {}) {
  const { instrumentColumns, setTradedPosition, instrumentsWithMetadata, isExpanded, disableActiveTrading, isEndVsStartDateComparison } = options
  const handleSetTradePosition = useCallback((position, instrumentType, path) => {
    const [positionToTrade, tradeInstrumentType] = getTradePosition(position, instrumentType, path)
    setTradedPosition(positionToTrade, [tradeInstrumentType, path])
  }, [setTradedPosition])

  const isCompareWeightStrategy = useMemo(
    () => instrumentsWithMetadata?.comparison?.type === RULES,
    [instrumentsWithMetadata],
  )

  const selectedMetrics = useMemo(() => {
    const defaultAssetViewMetrics = getDefaultAssetViewMetrics({ handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading })
    return computeMetricsFromDefinition(
      [...defaultAssetViewMetrics, ...metricsAndCategories],
      options,
    )
  }, [metricsAndCategories, options, handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading])

  return useMemo(() =>
    processAssetViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy),
  [selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy])
}

export function useFactorViewDashboardMetrics(metricsAndCategories, options = {}) {
  const { instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison } = options
  const selectedMetrics = useMemo(() => {
    return computeMetricsFromDefinition(
      [{ id: "factorLabel" }, ...metricsAndCategories],
      options,
    )
  }, [metricsAndCategories, options])

  const isWeightStrategy = useMemo(
    () => instrumentsWithMetadata?.comparison?.type === RULES,
    [instrumentsWithMetadata],
  )

  return useMemo(() =>
    processFactorViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isWeightStrategy),
  [selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isWeightStrategy])
}

export function useMetricSelectorMetrics(modelId, betaInstrument) {
  const isOpPro = useIsOpPro()
  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)

  const [contentSetMetrics, contentSetMetricsStatus] = useContentSetsMetadata()
  const [forecastMetrics, forecastMetricsStatus] = useResourcesList("forecast")
  const contentSets = map(contentSetMetrics, (contentSet) => {
    const metricOptions = contentSet.dataType === CONTENT_SET_DATA_TYPES.SENSITIVITY ? METRIC_OPTIONS.sensitivityContentSet : METRIC_OPTIONS.contentSet
    const filteredMetricOptions = metricOptions.filter(option => !option.disabled)
    return createMetricGroup(contentSet.id, contentSet, {
      metricOptions: filteredMetricOptions,
      name: contentSet.name,
    })
  })

  const [modelBetaFields, modelBetaFieldsStatus] = useModelBetaFields(modelId)
  const betaFields = useMemo(() => betaInstrument ? [...modelBetaFields, "dynamicPredicted"] : modelBetaFields, [betaInstrument, modelBetaFields])

  let status = combineTanStackStatuses([modelMetadataStatus, contentSetMetricsStatus, forecastMetricsStatus])

  const metricsWithOptions = useMemo(() => getRiskModelMetrics(modelMetadata, isOpPro), [modelMetadata, isOpPro])

  const { risk, exposure, predictedFactorSensitivity, historicalFactorSensitivity, performance } = useMemo(() =>
    metricsWithOptions.reduce((acc, { id, name, metricOptions, canAggregateCategories }) => {
      acc[id] = createMetricGroup(id, modelMetadata, { name, metricOptions, canAggregateCategories })
      return acc
    }, {}),
  [modelMetadata, metricsWithOptions])

  const marginalContribution = useMemo(() => {
    if (isOpPro) {
      return null
    }
    const marginalContributionMetrics = getMarginalContributionMetrics(modelMetadata)
    const categories = chain(marginalContributionMetrics)
      .groupBy("category")
      .map((metrics, category) => ({
        id: camelCase(category),
        label: category,
        items: metrics.map((metric) => ({
          ...metric,
          category,
        })),
      }))
      .value()

    return {
      id: "marginal",
      label: "Marginal Contribution",
      categories,
      items: categories.map((category) => ({
        ...category,
        items: category.items.map((item) => ({
          ...item,
          metricTypeOptions: createMetricTypeOptions([METRIC_OPTIONS.default], { prefix: "marginal", dataPath: item.dataPath, format: item.format }),
        })),
      })),
    }
  }, [modelMetadata, isOpPro])

  const beta = useMemo(() => ({
    id: "beta",
    label: "Beta",
    items: map(betaFields, (betaId) => ({
      id: betaId,
      label: BETA_FACTORS_WITH_DYNAMIC[betaId].label,
      category: "beta",
      metricTypeOptions: createMetricTypeOptions(METRIC_OPTIONS.beta, { prefix: "beta", factorId: betaId }),
    })),
  }), [betaFields])

  const performanceSummary = useMemo(() => ({
    id: "summary",
    label: "Summary",
    items: [
      ASSET_METRICS_BY_ID.totalReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.specificReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.factorsReturn({ category: "Performance Contribution" }),
      ASSET_METRICS_BY_ID.tradingReturn({ category: "Performance Contribution" }),
    ].map(metric => ({
      ...metric,
      metricTypeOptions: createMetricTypeOptions(METRIC_OPTIONS.performance, { prefix: "performance", factorId: metric.id }),
    })),
  }), [])

  const riskSummary = useMemo(() => ({
    id: "summary",
    label: "Summary",
    items: [
      ASSET_METRICS_BY_ID.riskTotalContribution(),
      ASSET_METRICS_BY_ID.riskSpecificContribution(),
      ASSET_METRICS_BY_ID.riskFactorsContribution(),
    ].map(metric => ({
      ...metric,
      metricTypeOptions: [{ ...METRIC_OPTIONS.default, dataPath: metric.dataPath, format: VALUE_FORMATS.PERCENT, prefix: "risk" }],
    })),
  }), [])

  const forecasts = useMemo(() => {
    const impliedReturns = {
      id: "impliedReturns",
      name: "Implied Returns",
    }

    const currentForecastMetrics = forecastMetrics || []
    const metricsWithImpliedReturns = isOpPro ? currentForecastMetrics : [...currentForecastMetrics, impliedReturns]
    const forecastMetricsWithMetrics = metricsWithImpliedReturns.map((forecast) => ({
      id: forecast.id,
      label: forecast.name,
      items: [
        ASSET_METRICS_BY_ID.securityExpectedReturn({ forecastId: forecast.id }),
        ASSET_METRICS_BY_ID.weightedExpectedReturn({ forecastId: forecast.id }),
      ],
    }))

    return {
      label: "Forecasts",
      items: forecastMetricsWithMetrics.map((forecast) => ({
        ...forecast,
        items: forecast.items.map((metric) => ({
          ...metric,
          category: forecast.name,
          metricTypeOptions: createMetricTypeOptions([METRIC_OPTIONS.default], { prefix: forecast.id, factorId: metric.id }),
        })),
      })),
    }
  }, [forecastMetrics, isOpPro])

  status = combineTanStackStatuses([modelMetadataStatus, contentSetMetricsStatus, modelBetaFieldsStatus])

  const metrics = useMemo(() => compact([
    risk ? { ...risk, items: [...(risk.items || []), riskSummary] } : null,
    marginalContribution,
    exposure,
    beta,
    predictedFactorSensitivity,
    historicalFactorSensitivity,
    forecasts,
    performance ? { ...performance, items: [...(performance.items || []), performanceSummary] } : null,
    ...contentSets,
  ]), [risk, exposure, beta, predictedFactorSensitivity, historicalFactorSensitivity, performance, performanceSummary, marginalContribution, contentSets, riskSummary, forecasts])

  return [metrics, status]
}

function getDefaultAssetViewMetrics({ handleSetTradePosition, instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument, isExpanded, disableActiveTrading }) {
  return [
    {
      id: "marketValue",
      shouldHide: () => instrumentColumns.some(elem => ["original", "rebalanced"].includes(elem)) && isInstrumentWithoutGMV(originalInstrument.type),
      filterInstrumentColumns: (instrumentColumns) => filterColumnsBasedOnGMV(instrumentColumns, originalInstrument, rebalancedInstrument, comparisonInstrument),
      actionHandler: !isExpanded ? handleSetTradePosition : null,
    },
    {
      id: "percentEquity",
      actionHandler: !isExpanded && !disableActiveTrading ? handleSetTradePosition : null,
    },
  ]
}

function transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy) {
  return {
    ...metric,
    id: `${metric.id}${capitalize(INSTRUMENTS[instrumentType].suffix)}`,
    prefix: metric.id,
    label: makeMetricLabel(metric, instrumentType, instrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy),
    dataPath: makeDataPath(metric.dataPath, INSTRUMENTS[instrumentType].metricsSuffix),
    baseDataPath: metric.dataPath,
    dataGetter: getDataGetter(metric, instrumentType, instrumentColumns),
    tooltipText: makeTooltipText(instrumentType, metric, instrumentsWithMetadata, isCompareWeightStrategy),
    isFirstInstrument: filteredInstrumentColumns?.length > 1 && index === 0,
    tooltipContents: makeTooltipContents(metric, { instrumentType }),
    downloadLabel: makeDownloadLabel(instrumentsWithMetadata, instrumentType, metric, isEndVsStartDateComparison, isCompareWeightStrategy),
    format: metric.format || VALUE_FORMATS.NUMBER,
    type: instrumentType,
  }
}

function processFactorViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy) {
  return chain(selectedMetrics)
    .map(metric => {
      if (metric.shouldHide) {
        return null
      }
      const filteredInstrumentColumns = getFilteredTriplicatedColumns(instrumentColumns, metric)
      return filteredInstrumentColumns?.map((instrumentType, index) =>
        transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy),
      )
    })
    .flatten()
    .compact()
    .value()
}

function processAssetViewMetrics(selectedMetrics, instrumentColumns, instrumentsWithMetadata, isEndVsStartDateComparison, isCompareWeightStrategy) {
  return chain([
    ...instrumentColumns.map(instrumentType => getDefaultDownloadMetrics(instrumentType)),
    ...selectedMetrics.map(metric => {
      if (metric.shouldHide && metric.shouldHide()) {
        return null
      }
      const filteredInstrumentColumns = getFilteredTriplicatedColumns(instrumentColumns, metric)
      return filteredInstrumentColumns?.map((instrumentType, index) => ({
        ...transformMetric(metric, instrumentType, index, instrumentColumns, instrumentsWithMetadata, filteredInstrumentColumns, isEndVsStartDateComparison, isCompareWeightStrategy),
        getAction: (position) => {
          if (!hasAction(metric.action, instrumentType, position, instrumentColumns)) {
            return null
          }
          return () => metric.action(position, instrumentType)
        },
      }))
    }),
  ])
    .flatten()
    .compact()
    .value()
}

function computeMetricsFromDefinition(metricsAndCategories, options) {
  return reduce(metricsAndCategories, (acc, metricOrCategory) => {
    if (metricOrCategory.id) {
      return [...acc, getMetric(metricOrCategory, options)]
    }
    return [...acc, ...getCategory(metricOrCategory, options)]
  }, [])
}

function getMetric(requestedMetric, options) {
  let metricDefinition = ASSET_METRICS_BY_ID[requestedMetric.id] || FACTOR_METRICS_BY_ID[requestedMetric.id]
  if (isFunction(metricDefinition)) {
    metricDefinition = metricDefinition({ ...requestedMetric, ...options })
  }

  return {
    ...requestedMetric,
    ...metricDefinition,
  }
}

function getCategory(item, options) {
  const category = ASSET_METRIC_CATEGORIES[item.category] || FACTOR_METRIC_CATEGORIES[item.category]
  if (isFunction(category)) {
    return category({ ...item, ...options })
  }
  return category
}
